import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Settings, ChevronDown, Store, Check, Building2, Send, Wallet, LogOut } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import useAccess from '../../hooks/useAccess';
import { SMIIFY_API } from '../../config/api';
import styles from './Navbar.module.css';
import ClosureReportModal from '../Reports/ClosureReportModal/ClosureReportModal';

const WALLET_API = `${SMIIFY_API}/wallet`;

// Page name for the breadcrumb (longest match first)
const PAGE_LABELS = [
  ['/dev/activate-stores', 'Activate stores'],
  ['/manage-loyalty-program', 'Loyalty'],
  ['/booking-history', 'Booking history'],
  ['/global-access', 'Branch access limits'],
  ['/wallet-billing', 'Store wallet'],
  ['/card-allotment', 'Card allotment'],
  ['/renew-loyalty', 'Renew loyalty'],
  ['/update-invoice', 'Edit bill'],
  ['/integrations', 'Integrations'],
  ['/appointments', 'Appointments'],
  ['/create-bill', 'Create bill'],
  ['/attendance', 'Attendance'],
  ['/marketing', 'Marketing'],
  ['/analytics', 'Analytics'],
  ['/inventory', 'Inventory'],
  ['/pricelist', 'Price list'],
  ['/invoices', 'Invoices'],
  ['/expenses', 'Expenses'],
  ['/feedback', 'Feedback'],
  ['/settings', 'Settings'],
  ['/payroll', 'Payroll'],
  ['/clients', 'Clients'],
  ['/booking', 'Booking'],
  ['/config', 'Config'],
  ['/staff', 'Staff'],
  ['/users', 'Users'],
];
const pageLabel = (path) => {
  if (path === '/') return 'Dashboard';
  const hit = PAGE_LABELS.find(([p]) => path === p || path.startsWith(`${p}/`));
  if (hit) return hit[1];
  const first = path.split('/').filter(Boolean)[0] || '';
  return first.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

const ROLE_LABEL = { SUPERADMIN: 'Owner (HQ)', ADMIN: 'Admin', MANAGER: 'Manager', RECEPTIONIST: 'Receptionist', STAFF: 'Staff' };

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeStore, availableStores, switchStore } = useStore();
  const { user, logout } = useAuth();
  const { can, isOwner } = useAccess();

  const [isStoreMenuOpen, setIsStoreMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isEODModalOpen, setIsEODModalOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [isWalletLoading, setIsWalletLoading] = useState(false);

  const dropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  const visibleStores = availableStores || [];
  const currentActiveId = activeStore?._id || activeStore?.id;
  const isHQActive = activeStore?.isHQ === true || currentActiveId === 'global_hq';

  // What this user may see (live access)
  const canSeeWallet = isHQActive ? isOwner : can('wallet.view');
  const canSendEod = !isHQActive && can('reports.view_eod_report');
  const canOpenSettings = isHQActive ? isOwner : can('settings.view');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsStoreMenuOpen(false);
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) setIsProfileMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Wallet balance: only asked for when the user may see it
  useEffect(() => {
    const token = localStorage.getItem('smiify_token');
    if (!activeStore || !token || !canSeeWallet) { setWalletBalance(null); return undefined; }
    let alive = true;
    const fetchId = isHQActive ? 'global_hq' : currentActiveId;
    (async () => {
      setIsWalletLoading(true);
      try {
        const res = await fetch(`${WALLET_API}/balance?storeId=${encodeURIComponent(fetchId)}`, {
          headers: { Authorization: `Bearer ${token}`, ...(isHQActive ? {} : { 'x-store-id': String(currentActiveId) }) },
        });
        const json = await res.json().catch(() => ({}));
        if (alive) setWalletBalance(res.ok && json.success && json.data ? Number(json.data.balance) || 0 : null);
      } catch (error) {
        if (alive) setWalletBalance(null);
      } finally {
        if (alive) setIsWalletLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [activeStore, currentActiveId, isHQActive, canSeeWallet]);

  const getAddressString = (store) => {
    if (!store) return '';
    if (store.isHQ) return store.address || 'Headquarters';
    const parts = [store.address, store.city, store.pincode].filter(Boolean);
    return parts.length ? parts.join(', ') : 'Address not mapped';
  };

  const displayName = user?.displayName || user?.fullName || user?.username || 'User';

  return (
    <>
      <header className={styles.navbar}>
        <div className={styles.leftSection}>
          <span className={styles.brandCrumb}>Smiify</span><span className={styles.separator}>/</span>
          <div className={styles.storeSelectorWrap} ref={dropdownRef}>
            <button className={`${styles.storeSelectorBtn} ${isStoreMenuOpen ? styles.activeBtn : ''}`} onClick={() => setIsStoreMenuOpen(!isStoreMenuOpen)}>
              {isHQActive ? <Building2 size={14} className={styles.hqIcon} /> : <Store size={14} className={styles.storeIcon} />}
              <span className={styles.storeName}>{activeStore ? (activeStore.storeName || activeStore.name || activeStore.partnerBusinessName) : 'Loading...'}</span>
              {visibleStores.length > 1 && <ChevronDown size={14} className={`${styles.chevronIcon} ${isStoreMenuOpen ? styles.chevronOpen : ''}`} />}
            </button>
            {isStoreMenuOpen && visibleStores.length > 1 && (
              <div className={styles.storeDropdown}>
                <div className={styles.dropdownHeader}>Switch Active Branch</div>
                <div className={styles.dropdownList}>
                  {visibleStores.map((store) => {
                    const storeId = store._id || store.id;
                    const isStoreHQ = store.isHQ;
                    return (
                      <button key={storeId} className={`${styles.storeOption} ${currentActiveId === storeId ? styles.activeOption : ''}`} onClick={() => { switchStore(storeId); setIsStoreMenuOpen(false); }}>
                        <div className={styles.storeOptionContent}>
                          <div className={styles.storeOptionTitle}>
                            {isStoreHQ ? <Building2 size={13} color="var(--h-color-primary-600, #673de6)" /> : <Store size={13} color="var(--h-color-neutral-500, #797980)" />}
                            <span className={styles.storeOptionName}>{store.storeName || store.name}</span>
                            {isStoreHQ && <span className={styles.hqBadge}>HQ</span>}
                          </div>
                          <div className={styles.storeOptionAddress}><span>{getAddressString(store)}</span></div>
                        </div>
                        {currentActiveId === storeId && <Check size={14} className={styles.activeCheck} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <span className={styles.separator}>/</span><span className={styles.pageCrumb}>{pageLabel(location.pathname)}</span>
        </div>

        <div className={styles.rightSection}>
          {canSeeWallet && (
            <button
              type="button"
              className={styles.walletBadge}
              title={isHQActive ? 'HQ master wallet balance' : 'Branch wallet balance'}
              onClick={() => navigate('/wallet-billing')}
              style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit' }}
            >
              <Wallet size={13} className={styles.walletIcon} />
              {isWalletLoading ? '...' : walletBalance === null ? '—' : `₹${walletBalance.toFixed(2)}`}
            </button>
          )}

          {canSendEod && (
            <button className={styles.eodBtn} onClick={() => setIsEODModalOpen(true)}>
              <Send size={13} /> Send EOD
            </button>
          )}

          {canOpenSettings && (
            <button className={styles.actionBtn} aria-label="Settings" title="Settings" onClick={() => navigate('/settings')}>
              <Settings size={16} />
            </button>
          )}
          <div className={styles.divider} />

          <div className={styles.profileDropdownWrap} ref={profileDropdownRef} onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}>
            <div className={styles.avatar}>{displayName.charAt(0).toUpperCase()}</div>
            <div className={styles.profileText}>
              <span className={styles.userName}>{displayName}</span>
              <span className={styles.userRole}>{ROLE_LABEL[user?.role] || user?.role || 'Staff'}</span>
            </div>
            <ChevronDown size={14} className={`${styles.dropdownIcon} ${isProfileMenuOpen ? styles.chevronOpen : ''}`} />

            {isProfileMenuOpen && (
              <div className={styles.profileMenu}>
                {canSeeWallet && (
                  <>
                    <button className={styles.profileMenuItem} onClick={() => { navigate('/wallet-billing'); setIsProfileMenuOpen(false); }}>
                      <Wallet size={14} color="var(--h-color-primary-600, #673de6)" /> Manage Credits
                    </button>
                    <div className={styles.profileMenuDivider} />
                  </>
                )}
                <button className={`${styles.profileMenuItem} ${styles.logoutItem}`} onClick={logout}>
                  <LogOut size={14} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {canSendEod && <ClosureReportModal isOpen={isEODModalOpen} onClose={() => setIsEODModalOpen(false)} />}
    </>
  );
};

export default Navbar;